/**
 * 3D pedestrian simulation component for React Three Fiber twin scenes.
 *
 * Renders stylized walking figures with animated limbs that navigate between
 * waypoints within configurable floor bounds. When a conference navigation
 * graph is supplied, routes use A* through door openings instead of
 * straight-line walks through walls.
 *
 * Coordinate convention: X = east, Z = south, Y = up (conference scene).
 * The civic twin scene uses Z = −north; callers must supply appropriate bounds.
 */

import { useRef, useMemo, useCallback, useEffect } from 'react';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import * as THREE from 'three';
import {
  ROOM_SEATING_GRIDS,
  type NavGraph,
  type PresentationRoomId,
  type RoomSeat,
  findNavWorldPath,
  findNearestNavNode,
} from '@/components/twin/conference-twin-data';

// ── Types ────────────────────────────────────────────────────────────────

/** Rectangular walkable area */
export interface WalkBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Readable role label for info popover */
export type PedestrianRole =
  | 'attendee'
  | 'exhibitor'
  | 'staff'
  | 'visitor'
  | 'speaker';

export const ALL_PEDESTRIAN_ROLES: readonly PedestrianRole[] = [
  'attendee',
  'exhibitor',
  'staff',
  'visitor',
  'speaker',
];

/** Avatar posture, independent of its navigation role. */
export type PedestrianPose = 'walking' | 'standing' | 'seated';

/** Complete state for one pedestrian */
export interface PedestrianState {
  id: string;
  position: THREE.Vector3;
  /** Immediate movement target (current path segment end) */
  targetPosition: THREE.Vector3;
  /**
   * Remaining path waypoints after `targetPosition`.
   * Final destination is the last entry (or targetPosition when empty).
   */
  pathWaypoints: THREE.Vector3[];
  /** Index into pathWaypoints for the next node after the current target */
  pathIndex: number;
  /** Current avatar posture. */
  pose: PedestrianPose;
  /** Posture applied once the active route reaches its final waypoint. */
  arrivalPose: PedestrianPose;
  /** Stable seat identifier when a presentation-room seat has been assigned. */
  seatId?: string;
  /** Heading applied while seated, in floor-plan radians. */
  seatFacing?: number;
  speed: number;
  color: string;
  animPhase: number;
  role: PedestrianRole;
  /** Name shown in info popover */
  label: string;
  /** Presence-specific metadata for a live user-controlled avatar. */
  livePresence?: {
    userId: string;
    accuracyMeters?: number | null;
  };
}

/** Metadata passed to the onSelect callback */
export interface PedestrianMeta {
  id: string;
  role: PedestrianRole;
  label: string;
  color: string;
  position: [number, number, number];
}

/** Schedule-derived behavior for one presentation room. */
export interface PedestrianTimelineRoomTarget {
  roomId: PresentationRoomId;
  state: 'preparing' | 'live';
}

/** Conference event-clock state supplied by the schedule timeline controller. */
export interface PedestrianTimelineState {
  currentMinutes: number;
  roomTargets: readonly PedestrianTimelineRoomTarget[];
}

/** Props accepted by PedestrianSimulation */
export interface PedestrianSimulationProps {
  /** Current pedestrian state array — mutated in-place by the engine */
  pedestrians: PedestrianState[];
  /** Floor area pedestrians are allowed to walk within */
  bounds: WalkBounds;
  /**
   * Optional conference navigation graph. When set, destination picks snap to
   * graph nodes and routes use A* through doorways.
   */
  navGraph?: NavGraph | null;
  /** Optional conference schedule state that directs attendees and speakers. */
  timeline?: PedestrianTimelineState | null;
  /** Whether schedule-directed movement is currently active. */
  timelineSync?: boolean;
  /** Renders lightweight active-route debugging lines. */
  showPaths?: boolean;
  /** Limits which avatar roles are visible without stopping the simulation. */
  visibleRoles?: readonly PedestrianRole[];
  /** Currently highlighted pedestrian id (optional) */
  selectedId?: string | null;
  /** Called when a pedestrian is clicked */
  onSelect?: (meta: PedestrianMeta) => void;
  /** Global speed multiplier, default 1 */
  speedMultiplier?: number;
  /** Optional terrain sampler for scenes whose ground is not a flat plane. */
  groundElevation?: (x: number, z: number) => number;
}

// ── Palette & constants ──────────────────────────────────────────────────

const SKIN_COLOR = '#f5c9a6';
const ROLES = ALL_PEDESTRIAN_ROLES;
const ROLE_COLORS: Record<PedestrianRole, string[]> = {
  attendee: ['#3b82f6', '#6366f1', '#2563eb', '#4f46e5'],
  exhibitor: ['#f59e0b', '#d97706', '#eab308'],
  staff: ['#10b981', '#059669', '#34d399'],
  visitor: ['#8b5cf6', '#7c3aed', '#a78bfa'],
  speaker: ['#ef4444', '#dc2626', '#f87171'],
};

const FIRST_NAMES = [
  'Alex', 'Jordan', 'Taylor', 'Casey', 'Morgan', 'Riley', 'Quinn',
  'Avery', 'Skyler', 'Jamie', 'Rowan', 'Harper', 'Sage', 'Reese',
  'Finley', 'Dakota', 'Drew', 'Emery', 'Lennox', 'Phoenix',
];

/** Swing amplitude for limbs in radians */
const LIMB_SWING = 0.55;
/** Body bob amplitude */
const BOB_AMPLITUDE = 0.04;
/** Minimum distance to target before picking a new one */
const TARGET_THRESHOLD = 0.6;
/** Y offset for the entire figure so feet touch the ground plane */
const GROUND_Y = 0;
/** Body offset that places the hips at a typical chair-seat height. */
const SEATED_BODY_OFFSET = -0.3;
/** Relaxed arm angle used while seated. */
const SEATED_ARM_ANGLE = 0.3;
/** Hip and knee bends for the seated leg posture. */
const SEATED_HIP_BEND = Math.PI / 2;
const SEATED_KNEE_BEND = -Math.PI / 2;

// ── Helpers ──────────────────────────────────────────────────────────────

function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)] as T;
}

function randomWaypoint(bounds: WalkBounds): THREE.Vector3 {
  return new THREE.Vector3(
    randomInRange(bounds.minX + 1, bounds.maxX - 1),
    GROUND_Y,
    randomInRange(bounds.minZ + 1, bounds.maxZ - 1),
  );
}

/**
 * A* pathfinding over a nav graph between two world positions.
 * Returns an ordered list of floor waypoints (Y ignored / set by caller).
 */
export function findPathAStar(
  graph: NavGraph,
  startX: number,
  startZ: number,
  goalX: number,
  goalZ: number,
): Array<{ x: number; z: number }> {
  return findNavWorldPath(graph, startX, startZ, goalX, goalZ);
}

/** Pick a random destination — prefers graph nodes when a nav graph is present. */
function pickDestination(bounds: WalkBounds, navGraph?: NavGraph | null): THREE.Vector3 {
  if (navGraph && navGraph.nodes.length > 0) {
    // Bias toward corridors / POIs / rooms so traffic uses the full floor
    const roll = Math.random();
    let pool = navGraph.nodes;
    if (roll < 0.45) {
      const corridors = navGraph.nodes.filter(
        (n) => n.kind === 'corridor' || n.kind === 'poi' || n.kind === 'door-approach',
      );
      if (corridors.length > 0) pool = corridors;
    } else if (roll < 0.75) {
      const rooms = navGraph.nodes.filter((n) => n.kind === 'room');
      if (rooms.length > 0) pool = rooms;
    }
    const node = pickRandom(pool);
    return new THREE.Vector3(node.x, GROUND_Y, node.z);
  }
  return randomWaypoint(bounds);
}

/**
 * Assign a multi-waypoint route from the pedestrian's current position to a
 * destination. Mutates `targetPosition`, `pathWaypoints`, and `pathIndex`.
 */
export interface PedestrianRouteOptions {
  /** Posture to apply when the final waypoint is reached. */
  arrivalPose?: PedestrianPose;
  /** Fixed heading used by a seated avatar after arrival. */
  seatFacing?: number;
  /** Stable presentation-room seat identifier, if this route reserves one. */
  seatId?: string;
}

export function assignPedestrianRoute(
  ped: PedestrianState,
  destination: THREE.Vector3,
  navGraph?: NavGraph | null,
  options: PedestrianRouteOptions = {},
): void {
  ped.pose = 'walking';
  ped.arrivalPose = options.arrivalPose ?? 'standing';
  ped.seatFacing = options.seatFacing;
  ped.seatId = options.seatId;
  if (navGraph && navGraph.nodes.length > 0) {
    const pts = findPathAStar(
      navGraph,
      ped.position.x,
      ped.position.z,
      destination.x,
      destination.z,
    );
    if (pts.length === 0) {
      ped.targetPosition.set(destination.x, GROUND_Y, destination.z);
      ped.pathWaypoints = [];
      ped.pathIndex = 0;
      return;
    }
    const vectors = pts.map((p) => new THREE.Vector3(p.x, GROUND_Y, p.z));
    ped.targetPosition.copy(vectors[0]!);
    ped.pathWaypoints = vectors.slice(1);
    ped.pathIndex = 0;
    return;
  }

  ped.targetPosition.copy(destination);
  ped.pathWaypoints = [];
  ped.pathIndex = 0;
}

/** Route an avatar to a presentation-room seat and switch to its seated pose on arrival. */
export function assignPedestrianSeat(
  ped: PedestrianState,
  seat: RoomSeat,
  navGraph?: NavGraph | null,
): void {
  assignPedestrianRoute(
    ped,
    new THREE.Vector3(seat.x, GROUND_Y, seat.z),
    navGraph,
    {
      arrivalPose: 'seated',
      seatFacing: seat.facing,
      seatId: seat.id,
    },
  );
}

/** Advance to the next path segment, or signal that the full route is done. */
function advancePath(ped: PedestrianState): boolean {
  if (ped.pathIndex < ped.pathWaypoints.length) {
    ped.targetPosition.copy(ped.pathWaypoints[ped.pathIndex]!);
    ped.pathIndex += 1;
    return true;
  }
  return false;
}

function pickBreakDestination(bounds: WalkBounds, navGraph?: NavGraph | null): THREE.Vector3 {
  if (navGraph) {
    const circulationNodes = navGraph.nodes.filter(
      (node) => node.kind === 'corridor' || node.kind === 'poi' || node.kind === 'door-approach',
    );
    if (circulationNodes.length > 0) {
      const node = pickRandom(circulationNodes);
      return new THREE.Vector3(node.x, GROUND_Y, node.z);
    }
  }
  return pickDestination(bounds, navGraph);
}

function isTimelineParticipant(ped: PedestrianState): boolean {
  return ped.role === 'attendee' || ped.role === 'speaker';
}

interface TimelineSeatAssignment {
  ped: PedestrianState;
  seat: RoomSeat;
  state: PedestrianTimelineRoomTarget['state'];
}

function buildTimelineSeatAssignments(
  pedestrians: PedestrianState[],
  roomTargets: readonly PedestrianTimelineRoomTarget[],
): TimelineSeatAssignment[] {
  const targets = [...roomTargets].sort((a, b) => a.roomId.localeCompare(b.roomId));
  if (targets.length === 0) return [];

  const participants = pedestrians.filter(isTimelineParticipant);
  const speakers = participants
    .filter((ped) => ped.role === 'speaker')
    .sort((a, b) => a.id.localeCompare(b.id));
  const attendees = participants
    .filter((ped) => ped.role === 'attendee')
    .sort((a, b) => a.id.localeCompare(b.id));
  const nextSeatIndex = new Map<PresentationRoomId, number>();

  const takeSeat = (target: PedestrianTimelineRoomTarget): RoomSeat | null => {
    const seats = ROOM_SEATING_GRIDS[target.roomId];
    const nextIndex = nextSeatIndex.get(target.roomId) ?? 0;
    if (nextIndex >= seats.length) return null;
    nextSeatIndex.set(target.roomId, nextIndex + 1);
    return seats[nextIndex] ?? null;
  };

  const assignments: TimelineSeatAssignment[] = [];
  for (let index = 0; index < speakers.length; index += 1) {
    const ped = speakers[index]!;
    const target = targets[index % targets.length]!;
    const seat = takeSeat(target);
    if (seat) assignments.push({ ped, seat, state: target.state });
  }

  const remainingSeats: Array<{ seat: RoomSeat; state: PedestrianTimelineRoomTarget['state'] }> = [];
  let foundSeat = true;
  while (foundSeat) {
    foundSeat = false;
    for (const target of targets) {
      const seat = takeSeat(target);
      if (seat) {
        remainingSeats.push({ seat, state: target.state });
        foundSeat = true;
      }
    }
  }

  for (let index = 0; index < attendees.length && index < remainingSeats.length; index += 1) {
    const ped = attendees[index]!;
    const assignment = remainingSeats[index]!;
    assignments.push({ ped, seat: assignment.seat, state: assignment.state });
  }

  return assignments;
}

function synchronizeTimelineRoutes(
  pedestrians: PedestrianState[],
  bounds: WalkBounds,
  navGraph: NavGraph | null | undefined,
  timeline: PedestrianTimelineState,
): void {
  const assignments = buildTimelineSeatAssignments(pedestrians, timeline.roomTargets);
  const assignedIds = new Set(assignments.map(({ ped }) => ped.id));

  for (const { ped, seat, state } of assignments) {
    const arrivalPose: PedestrianPose = state === 'live' ? 'seated' : 'standing';
    if (ped.seatId === seat.id && ped.arrivalPose === arrivalPose) continue;

    assignPedestrianRoute(
      ped,
      new THREE.Vector3(seat.x, GROUND_Y, seat.z),
      navGraph,
      {
        arrivalPose,
        seatFacing: seat.facing,
        seatId: seat.id,
      },
    );
  }

  for (const ped of pedestrians) {
    if (!isTimelineParticipant(ped) || assignedIds.has(ped.id)) continue;
    if (!ped.seatId && ped.arrivalPose !== 'seated' && ped.pose !== 'seated') continue;
    assignPedestrianRoute(ped, pickBreakDestination(bounds, navGraph), navGraph);
  }
}

let pedestrianIdCounter = 0;

/** Create a new pedestrian with random attributes at a random position */
export function createPedestrian(
  bounds: WalkBounds,
  overrides?: Partial<Pick<PedestrianState, 'role' | 'speed' | 'color' | 'label'>> & {
    navGraph?: NavGraph | null;
  },
): PedestrianState {
  const role = overrides?.role ?? pickRandom(ROLES);
  const color = overrides?.color ?? pickRandom(ROLE_COLORS[role]);
  const label = overrides?.label ?? pickRandom(FIRST_NAMES);
  const navGraph = overrides?.navGraph ?? null;
  pedestrianIdCounter += 1;

  const position = pickDestination(bounds, navGraph);
  // Prefer spawning on graph nodes so first route starts on the network
  if (navGraph) {
    const nearest = findNearestNavNode(navGraph, position.x, position.z);
    if (nearest) position.set(nearest.x, GROUND_Y, nearest.z);
  }

  const ped: PedestrianState = {
    id: `ped-${pedestrianIdCounter}-${Date.now()}`,
    position,
    targetPosition: position.clone(),
    pathWaypoints: [],
    pathIndex: 0,
    pose: 'standing',
    arrivalPose: 'standing',
    speed: overrides?.speed ?? randomInRange(1.2, 2.4),
    color,
    animPhase: Math.random() * Math.PI * 2,
    role,
    label,
  };

  assignPedestrianRoute(ped, pickDestination(bounds, navGraph), navGraph);
  return ped;
}

// ── Limb geometry (shared across all instances via useMemo) ──────────────

function useSharedGeometry() {
  return useMemo(() => {
    const head = new THREE.SphereGeometry(0.22, 12, 10);
    const torso = new THREE.BoxGeometry(0.4, 0.5, 0.22);
    const limb = new THREE.BoxGeometry(0.12, 0.42, 0.12);
    const leg = new THREE.BoxGeometry(0.12, 0.34, 0.12);
    const shoe = new THREE.BoxGeometry(0.14, 0.08, 0.22);
    return { head, torso, limb, leg, shoe };
  }, []);
}

// ── Single pedestrian mesh ───────────────────────────────────────────────

interface PedestrianFigureProps {
  ped: PedestrianState;
  isSelected: boolean;
  onClick: (e: ThreeEvent<MouseEvent>) => void;
  geom: ReturnType<typeof useSharedGeometry>;
  speedMultiplier: number;
  groundElevation?: (x: number, z: number) => number;
}

function PedestrianFigure({ ped, isSelected, onClick, geom, speedMultiplier, groundElevation }: PedestrianFigureProps) {
  const groupRef = useRef<THREE.Group>(null!);
  const leftLegRef = useRef<THREE.Group>(null!);
  const rightLegRef = useRef<THREE.Group>(null!);
  const leftKneeRef = useRef<THREE.Group>(null!);
  const rightKneeRef = useRef<THREE.Group>(null!);
  const leftArmRef = useRef<THREE.Group>(null!);
  const rightArmRef = useRef<THREE.Group>(null!);
  const bodyRef = useRef<THREE.Group>(null!);
  const ringRef = useRef<THREE.Mesh>(null!);
  const seatedBlendRef = useRef(0);

  /** Direction vector reused each frame */
  const _dir = useMemo(() => new THREE.Vector3(), []);

  useFrame((_state, delta) => {
    const p = ped;
    const clampedDelta = Math.min(delta, 0.05); // guard large frame gaps
    const seatedTarget = p.pose === 'seated' ? 1 : 0;
    seatedBlendRef.current = THREE.MathUtils.damp(
      seatedBlendRef.current,
      seatedTarget,
      10,
      clampedDelta,
    );
    const seatedBlend = seatedBlendRef.current;

    // ─ Movement ─────────────────────────────────────────────────────────
    _dir.copy(p.targetPosition).sub(p.position);
    _dir.y = 0;
    const dist = _dir.length();
    const isWalking = p.pose !== 'seated' && dist >= TARGET_THRESHOLD;

    if (isWalking) {
      _dir.normalize();
      const step = p.speed * speedMultiplier * clampedDelta;
      const actualStep = Math.min(step, dist);
      p.position.addScaledVector(_dir, actualStep);
      p.position.y = groundElevation?.(p.position.x, p.position.z) ?? GROUND_Y;

      // Face heading (rotate around Y)
      groupRef.current.rotation.y = Math.atan2(_dir.x, _dir.z);
    } else if (p.pose === 'seated' && p.seatFacing != null) {
      groupRef.current.rotation.y = THREE.MathUtils.damp(
        groupRef.current.rotation.y,
        p.seatFacing,
        12,
        clampedDelta,
      );
    }

    // Sync terrain height even while standing or seated.
    p.position.y = groundElevation?.(p.position.x, p.position.z) ?? GROUND_Y;
    // Sync Three.js group position regardless of posture.
    groupRef.current.position.copy(p.position);

    // ─ Walking / seated posture interpolation ───────────────────────────
    if (isWalking) {
      p.animPhase += clampedDelta * p.speed * speedMultiplier * 5;
    }
    const swing = isWalking ? Math.sin(p.animPhase) * LIMB_SWING : 0;
    const bob = isWalking ? Math.abs(Math.sin(p.animPhase)) * BOB_AMPLITUDE : 0;

    // A 90° hip bend and counter-rotated knee keep the lower leg vertical.
    leftLegRef.current.rotation.x = THREE.MathUtils.lerp(swing, SEATED_HIP_BEND, seatedBlend);
    rightLegRef.current.rotation.x = THREE.MathUtils.lerp(-swing, SEATED_HIP_BEND, seatedBlend);
    leftKneeRef.current.rotation.x = THREE.MathUtils.lerp(0, SEATED_KNEE_BEND, seatedBlend);
    rightKneeRef.current.rotation.x = THREE.MathUtils.lerp(0, SEATED_KNEE_BEND, seatedBlend);
    // Arms settle on the lap and the torso lowers onto the chair height.
    leftArmRef.current.rotation.x = THREE.MathUtils.lerp(-swing * 0.7, SEATED_ARM_ANGLE, seatedBlend);
    rightArmRef.current.rotation.x = THREE.MathUtils.lerp(swing * 0.7, SEATED_ARM_ANGLE, seatedBlend);
    bodyRef.current.position.y = THREE.MathUtils.lerp(bob, SEATED_BODY_OFFSET, seatedBlend);

    // Selection ring pulse
    ringRef.current.visible = isSelected;
    if (isSelected) {
      const pulse = 1 + Math.sin(p.animPhase * 2) * 0.08;
      ringRef.current.scale.set(pulse, 1, pulse);
    }
  });

  const bodyColor = useMemo(() => new THREE.Color(ped.color), [ped.color]);
  const skinMat = useMemo(() => new THREE.Color(SKIN_COLOR), []);
  const selColor = useMemo(() => new THREE.Color('#facc15'), []);
  const accuracyRingScale = ped.livePresence?.accuracyMeters == null
    ? 0
    : THREE.MathUtils.clamp(ped.livePresence.accuracyMeters / 5, 0.55, 4);

  return (
    <group
      ref={groupRef}
      position={[ped.position.x, ped.position.y, ped.position.z]}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
    >
      {ped.livePresence && accuracyRingScale > 0 && (
        <mesh rotation-x={-Math.PI / 2} position-y={0.015} scale={[accuracyRingScale, accuracyRingScale, accuracyRingScale]}>
          <ringGeometry args={[0.56, 0.62, 40]} />
          <meshBasicMaterial color={bodyColor} transparent opacity={0.32} side={THREE.DoubleSide} depthWrite={false} />
        </mesh>
      )}
      {ped.livePresence && (
        <Billboard position={[0, 2.05, 0]}>
          <Text fontSize={0.22} color="#ffffff" outlineWidth={0.025} outlineColor="#111827" anchorX="center" anchorY="middle">
            {ped.label}
          </Text>
        </Billboard>
      )}

      {/* Selection ring at feet */}
      <mesh ref={ringRef} rotation-x={-Math.PI / 2} position-y={0.02} visible={isSelected}>
        <ringGeometry args={[0.38, 0.5, 24]} />
        <meshBasicMaterial color={selColor} transparent opacity={0.85} side={THREE.DoubleSide} />
      </mesh>

      {/* Body group lowers and relaxes into the seated posture. */}
      <group ref={bodyRef}>
        <mesh geometry={geom.head} position={[0, 1.52, 0]} castShadow>
          <meshStandardMaterial color={skinMat} roughness={0.7} />
        </mesh>

        <mesh geometry={geom.torso} position={[0, 1.1, 0]} castShadow>
          <meshStandardMaterial color={bodyColor} roughness={0.6} />
        </mesh>

        <group ref={leftArmRef} position={[0.26, 1.28, 0]}>
          <mesh geometry={geom.limb} position={[0, -0.22, 0]} castShadow>
            <meshStandardMaterial color={bodyColor} roughness={0.6} />
          </mesh>
        </group>

        <group ref={rightArmRef} position={[-0.26, 1.28, 0]}>
          <mesh geometry={geom.limb} position={[0, -0.22, 0]} castShadow>
            <meshStandardMaterial color={bodyColor} roughness={0.6} />
          </mesh>
        </group>

        {/* Two-segment legs support both walking and 90° seated hip/knee bends. */}
        <group ref={leftLegRef} position={[0.1, 0.82, 0]}>
          <mesh geometry={geom.leg} position={[0, -0.17, 0]} castShadow>
            <meshStandardMaterial color="#374151" roughness={0.7} />
          </mesh>
          <group ref={leftKneeRef} position={[0, -0.34, 0]}>
            <mesh geometry={geom.leg} position={[0, -0.17, 0]} castShadow>
              <meshStandardMaterial color="#374151" roughness={0.7} />
            </mesh>
            <mesh geometry={geom.shoe} position={[0, -0.37, 0.05]} castShadow>
              <meshStandardMaterial color="#1f2937" roughness={0.8} />
            </mesh>
          </group>
        </group>

        <group ref={rightLegRef} position={[-0.1, 0.82, 0]}>
          <mesh geometry={geom.leg} position={[0, -0.17, 0]} castShadow>
            <meshStandardMaterial color="#374151" roughness={0.7} />
          </mesh>
          <group ref={rightKneeRef} position={[0, -0.34, 0]}>
            <mesh geometry={geom.leg} position={[0, -0.17, 0]} castShadow>
              <meshStandardMaterial color="#374151" roughness={0.7} />
            </mesh>
            <mesh geometry={geom.shoe} position={[0, -0.37, 0.05]} castShadow>
              <meshStandardMaterial color="#1f2937" roughness={0.8} />
            </mesh>
          </group>
        </group>
      </group>
    </group>
  );
}

// ── Simulation engine (path segment advance + destination reassignment) ──

interface SimulationEngineProps {
  pedestrians: PedestrianState[];
  bounds: WalkBounds;
  navGraph?: NavGraph | null;
  timeline?: PedestrianTimelineState | null;
  timelineSync: boolean;
  groundElevation?: (x: number, z: number) => number;
}

function SimulationEngine({ pedestrians, bounds, navGraph, timeline, timelineSync, groundElevation }: SimulationEngineProps) {
  const _tmpDir = useMemo(() => new THREE.Vector3(), []);
  const previousTimelineSyncRef = useRef(timelineSync);

  useEffect(() => {
    if (timeline) synchronizeTimelineRoutes(pedestrians, bounds, navGraph, timeline);
  }, [bounds, navGraph, pedestrians, timeline]);

  useEffect(() => {
    if (!timelineSync && previousTimelineSyncRef.current) {
      for (const ped of pedestrians) {
        if (!isTimelineParticipant(ped) || (!ped.seatId && ped.pose !== 'seated')) continue;
        assignPedestrianRoute(ped, pickBreakDestination(bounds, navGraph), navGraph);
      }
    }
    previousTimelineSyncRef.current = timelineSync;
  }, [bounds, navGraph, pedestrians, timelineSync]);

  useFrame(() => {
    for (const ped of pedestrians) {
      if (ped.livePresence) {
        // Live avatars move only toward their reported device position. Their
        // interpolation and walking pose are handled by PedestrianFigure.
        ped.position.y = groundElevation?.(ped.position.x, ped.position.z) ?? GROUND_Y;
        continue;
      }

      _tmpDir.copy(ped.targetPosition).sub(ped.position);
      _tmpDir.y = 0;
      const dist = _tmpDir.length();

      if (dist < TARGET_THRESHOLD) {
        // Prefer the next path node. A seat is a terminal destination until
        // another caller explicitly assigns a new route (for example, when a
        // presentation finishes on the schedule timeline).
        if (advancePath(ped)) {
          ped.pose = 'walking';
        } else if (ped.arrivalPose === 'seated') {
          ped.pose = 'seated';
        } else if (ped.seatId) {
          // Early arrivals wait at their allocated seat until the session becomes live.
          ped.pose = 'standing';
        } else {
          ped.pose = 'standing';
          assignPedestrianRoute(ped, pickDestination(bounds, navGraph), navGraph);
        }
      }

      // Soft clamp — keep strays inside the floor envelope
      ped.position.x = Math.max(bounds.minX, Math.min(bounds.maxX, ped.position.x));
      ped.position.z = Math.max(bounds.minZ, Math.min(bounds.maxZ, ped.position.z));
      ped.position.y = groundElevation?.(ped.position.x, ped.position.z) ?? GROUND_Y;
    }
  });

  return null;
}

// ── Route visualization ─────────────────────────────────────────────────

const MAX_PATH_LINE_SEGMENTS_PER_PEDESTRIAN = 128;

function PedestrianPathLines({ pedestrians }: { pedestrians: readonly PedestrianState[] }) {
  const { geometry, line } = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(
        new Float32Array(
          Math.max(pedestrians.length, 1) * MAX_PATH_LINE_SEGMENTS_PER_PEDESTRIAN * 2 * 3,
        ),
        3,
      ),
    );
    const material = new THREE.LineBasicMaterial({
      color: '#38bdf8',
      transparent: true,
      opacity: 0.8,
      depthTest: false,
    });
    const line = new THREE.LineSegments(geometry, material);
    line.frustumCulled = false;
    line.renderOrder = 1;
    return { geometry, line };
  }, [pedestrians.length]);

  useEffect(
    () => () => {
      geometry.dispose();
      (line.material as THREE.Material).dispose();
    },
    [geometry, line],
  );

  useFrame(() => {
    const attribute = geometry.getAttribute('position') as THREE.BufferAttribute;
    let pointIndex = 0;
    const maxPoints = attribute.count;
    const writeSegment = (start: THREE.Vector3, end: THREE.Vector3) => {
      if (pointIndex + 1 >= maxPoints) return false;
      attribute.setXYZ(pointIndex, start.x, start.y + 0.08, start.z);
      attribute.setXYZ(pointIndex + 1, end.x, end.y + 0.08, end.z);
      pointIndex += 2;
      return true;
    };

    for (const ped of pedestrians) {
      if (ped.pose !== 'walking') continue;
      let previous = ped.position;
      if (!writeSegment(previous, ped.targetPosition)) break;
      previous = ped.targetPosition;
      for (let index = ped.pathIndex; index < ped.pathWaypoints.length; index += 1) {
        const next = ped.pathWaypoints[index]!;
        if (!writeSegment(previous, next)) break;
        previous = next;
      }
    }
    attribute.needsUpdate = true;
    geometry.setDrawRange(0, pointIndex);
  });

  return <primitive object={line} />;
}

// ── Main exported component ──────────────────────────────────────────────

/**
 * Renders and animates a group of 3D pedestrians within a React Three Fiber
 * Canvas. Must be placed inside a `<Canvas>` subtree.
 *
 * With `navGraph`, pedestrians follow A* node paths through door openings.
 * Without it, they walk straight between random points inside `bounds`.
 */
export function PedestrianSimulation({
  pedestrians,
  bounds,
  navGraph = null,
  timeline = null,
  timelineSync = false,
  showPaths = false,
  visibleRoles = ALL_PEDESTRIAN_ROLES,
  selectedId,
  onSelect,
  speedMultiplier = 1,
  groundElevation,
}: PedestrianSimulationProps) {
  const geom = useSharedGeometry();
  const visiblePedestrians = useMemo(
    () => pedestrians.filter((ped) => visibleRoles.includes(ped.role)),
    [pedestrians, pedestrians.length, visibleRoles],
  );

  const handleClick = useCallback(
    (ped: PedestrianState) => (_e: ThreeEvent<MouseEvent>) => {
      onSelect?.({
        id: ped.id,
        role: ped.role,
        label: ped.label,
        color: ped.color,
        position: [ped.position.x, ped.position.y, ped.position.z],
      });
    },
    [onSelect],
  );

  return (
    <group name="pedestrian-simulation">
      <SimulationEngine
        pedestrians={pedestrians}
        bounds={bounds}
        navGraph={navGraph}
        timeline={timeline}
        timelineSync={timelineSync}
        groundElevation={groundElevation}
      />
      {showPaths ? <PedestrianPathLines pedestrians={visiblePedestrians} /> : null}
      {visiblePedestrians.map((ped) => (
          <PedestrianFigure
            key={ped.id}
            ped={ped}
            isSelected={selectedId === ped.id}
            onClick={handleClick(ped)}
            geom={geom}
            speedMultiplier={speedMultiplier}
            groundElevation={groundElevation}
          />
        ))}
    </group>
  );
}
